import {
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { TenantPage } from 'components/common';
import FileUploader from 'components/common/file-uploader';
import SectionTitle from 'components/common/SectionTitle';
import EntityHistory from 'components/widgets/EntityHistory';
import toast from 'features/toast';
import { IEquipment, IContractEquipment } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { equipmentHistoryLineService } from 'services/api';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EquipmentForm from '../EquipmentForm';
import EquipmentToCostDialog from '../EquipmentToCostDialog';
import ConfirmationUnlinkButtonDialog from 'components/helpers/ConfirmationButtonUnlink';
import { DocumentTableCardComponent } from 'components/common/documentLM/DocumentTableCardComponent';
import { isTechnicianCoordinator } from 'components/helpers/IsTechnicianCoordinator';
import { isDirector } from 'components/helpers/IsDirector';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';
import { HistoryLineList } from 'components/common/HistoryList/list/HisotryLineList';
import { EquipmentHistoryLineColumns } from 'components/common/enhanced/common-headers/equipment-history-lines';
import { DataGridPremium, GridColumns } from '@mui/x-data-grid-premium';
import { EquipmentAccessorColumns } from 'components/common/enhanced/common-headers/equipment-accessories';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import { booleanCell } from 'components/common/enhanced/cells/boolean-cell';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { useAppSelector } from 'app/store';
import { ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from '../../../../constants';
import { equipmentApi } from '../api';
import { EquipmentLinkMode } from '../types';

const downloadableFields = [
  'technical_file',
  'user_manual',
  'ce_ivd',
  'maintenance_checklist_template',
  'preemptive_maintenance_calendar'
];

export const EditEquipment = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.equipment.edit';
  const { equipmentId } = useParams();
  const groups = useAppSelector((state) => state.auth.groups);
  const hideTenantAdminSections =
    groups.includes(ROLE_TENANT_ADMIN) && !groups.includes(ROLE_SUPER_ADMIN);

  const [equipment, setEquipment] = useState<IEquipment>();
  const [contractEquipments, setContractEquipments] = useState<IContractEquipment[]>([]);
  const [mode, setMode] = useState<EquipmentLinkMode>('');

  const isDisabledEdit =
    isTechnicianCoordinator() || isTechnician() || isDirector() || isSupplier();

  const fetchEquipment = () => {
    equipmentApi.getById(+equipmentId).then((res) => {
      setEquipment(res);
    });
  };

  useEffect(() => {
    fetchEquipment();
    equipmentApi.getContractEquipments(+equipmentId).then(setContractEquipments);
  }, [equipmentId]);

  // -------------------------------------- UPDATE MANAGEMENT
  const updateEquipment = async (values) => {
    if (!equipment) return;
    equipmentApi.update(equipment.id, values).then(() => {
      toast.success('Strumento aggiornato con successo');
    });
  };
  // -------------------------------------- REMOVE COST LINK MANAGEMENT
  const handleRemoveLink = (id: number, pars: string) => {
    equipmentApi.removeCostLink(+equipmentId, pars, id).then(() => {
      fetchEquipment();
      toast.success(t(translationPrefix + '.price-origin-removed-success'));
    });
  };

  const columns: GridColumns = EquipmentAccessorColumns();
  const contractColumns: GridColumns = [
    {
      field: 'contract_header_code',
      headerName: t('entities.contract-equipment.contract_code'),
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'maintenance_open',
      headerName: t('entities.contract-equipment.maintenance_open'),
      width: 200,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'ticketing_open',
      headerName: t('entities.contract-equipment.ticketing_open'),
      width: 200,
      type: 'boolean',
      renderCell: booleanCell(t)
    }
  ];

  // -------------------------------------- HISTORY MANAGEMENT
  const historyRefresher = useRef(null);
  const handleDateTimeUpdate = (id: number, datetime: string) => {
    equipmentApi.updateHistoryLine(id, datetime).then(() => {
      toast.success(t('actions.successfully.updated'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const handleDeleteLine = (id: number) => {
    equipmentApi.deleteHistoryLine(id).then(() => {
      toast.success(t('actions.successfully.deleted'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const manualRefresh = () => {
    if (historyRefresher.current) {
      historyRefresher.current(); // ✅ Chiama la funzione di refresh se esiste
    }
  };

  const historyColumns = EquipmentHistoryLineColumns(handleDateTimeUpdate, handleDeleteLine, false);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={equipment?.code}>
      {equipment && (
        <div className="flex flex-wrap">
          <EquipmentForm
            equipment={equipment}
            onSubmit={updateEquipment}
            disableEdit={isDisabledEdit}
          />
          <IsTenantAdministrator>
            <div className="w-full mt-4">
              <SectionTitle>{t(`global.contracts`)}</SectionTitle>
              <Card variant="outlined">
                <CardContent>
                  <DataGridPremium
                    density="compact"
                    rows={contractEquipments}
                    columns={contractColumns}
                    sx={{ border: 'none' }}
                    disableSelectionOnClick
                  />
                </CardContent>
              </Card>
            </div>
          </IsTenantAdministrator>
          {!hideTenantAdminSections && (
            <div className="w-full mb-4 mt-4">
              <SectionTitle>{t(`entities.equipment.accessories`)}</SectionTitle>
              <Card variant="outlined">
                <CardContent>
                  <DataGridPremium
                    density="compact"
                    rows={equipment.accessorList}
                    columns={columns}
                    sx={{ border: 'none' }}
                    disableSelectionOnClick
                  />
                </CardContent>
              </Card>
            </div>
          )}

          <div className="py-4 w-full mb-4">
            <SectionTitle>{t(`global.attachments`)}</SectionTitle>
            <IsTenantAdministrator>
              <Card variant="outlined">
                <CardContent>
                  <div className="flex flex-wrap">
                    {downloadableFields.map((field, i) => (
                      <div key={i} className={`w-1/5`}>
                        <span>{t(`entities.equipment.${field}`)}:</span>
                        <FileUploader
                          service={equipmentApi.listService}
                          entityId={equipment.id}
                          attachmentName={field}
                          filename={equipment[field]}
                          disableEdit={isDisabledEdit}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </IsTenantAdministrator>
          </div>

          {!hideTenantAdminSections && (
            <>
              <div className="w-full p-4 flex flex-wrap">
                <div className="w-full">
                  <SectionTitle>{t('global.costs')}</SectionTitle>
                </div>
                {equipment &&
                  [
                    { field: 'laboratories', link: '/tenant/topology/lab/' },
                    { field: 'operationalunits', link: '/tenant/topology/operationalunit/' },
                    { field: 'sectors', link: '/tenant/topology/sector/' },
                    { field: 'workstations', link: '/tenant/cost-analysis/workstations/' },
                    { field: 'exams', link: '/tenant/cost-analysis/exams/' }
                  ].map((e) => (
                    <div
                      key={e.field}
                      className="w-full sm:w-1/2 lg:w-1/5 pl-4 first:pl-0 border-r last:border-0">
                      <div className="flex justify-start items-center mt-4 mb-2">
                        <Typography variant="body1" component="div" fontWeight={700}>
                          {t(`global.${e.field}`)}
                        </Typography>
                        {!isDisabledEdit && (
                          <IconButton
                            color="primary"
                            onClick={() => setMode(e.field as EquipmentLinkMode)}>
                            <AddCircleOutlineIcon />
                          </IconButton>
                        )}
                      </div>
                      <List dense={true} className="ml-0">
                        {equipment[e.field] &&
                          equipment[e.field].map((entity, i) => (
                            <ListItem
                              key={i}
                              sx={{ padding: 0 }}
                              secondaryAction={
                                <div key={i}>
                                  <NavLink to={`${e.link}${entity.id}`}>
                                    <IconButton edge="end">
                                      <VisibilityIcon />
                                    </IconButton>
                                  </NavLink>
                                  <ConfirmationUnlinkButtonDialog
                                    handleUnlink={handleRemoveLink}
                                    id={entity.id}
                                    pars={e.field}
                                    iconButton={true}
                                  />
                                </div>
                              }>
                              <ListItemButton>
                                <ListItemText
                                  primary={entity.code}
                                  secondary={entity['description']}
                                />
                                <Divider variant="inset" component="li" />
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </div>
                  ))}
              </div>
              <EquipmentToCostDialog
                equipmentId={+equipmentId}
                mode={mode}
                setMode={setMode}
                callback={fetchEquipment}
              />
            </>
          )}

          <div className="w-full p-4">
            <Card variant="outlined">
              <DocumentTableCardComponent
                originalService={'equipment'}
                objectId={equipment.id}
                dictParams={{ equipment: equipmentId }}
                folder={'menu.equipments/' + equipment.code}
                folder_description={`Descrizione: ${equipment.description}; Seriale: ${equipment.serial_number}; `}
                disableEdit={isDisabledEdit}
              />
            </Card>
          </div>

          {!hideTenantAdminSections && (
            <>
              <div className="w-full py-2">
                <Card variant="outlined">
                  <CardContent>
                    <HistoryLineList
                      service={equipmentHistoryLineService}
                      columns={historyColumns}
                      outerParams={{ equipment_code: equipment.code }}
                      refresh={historyRefresher}
                      manualRefresh={manualRefresh}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="w-full md:w-1/2 pr-4">
                <Card variant="outlined">
                  <CardContent>
                    <EntityHistory service={equipmentApi.listService} entityId={equipment.id} />
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}
    </TenantPage>
  );
};

export default EditEquipment;
